/* eslint-disable */
import React, { useState, useContext, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Box,
  Button,
  Chip,
} from "@material-ui/core";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from "@material-ui/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import AuthContext from "contexts/AuthProvider.js";
import { client } from "../../../../services/axiosClient.js";
import { ThreeDots } from "react-loader-spinner";

// Componente para renderizar o status com farol
function StatusIndicator({ status, size = "medium" }) {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "green":
        return "#4caf50";
      case "yellow":
        return "#ff9800";
      case "red":
        return "#f44336";
      case "gray":
      default:
        return "#9e9e9e";
    }
  };

  const getStatusStyles = (status, isHovered = false) => {
    const color = getStatusColor(status);
    return {
      width: size === "small" ? "10px" : "14px",
      height: size === "small" ? "10px" : "14px",
      borderRadius: "50%",
      backgroundColor: color,
      display: "inline-block",
      margin: "0 auto",
      transition: "all 0.2s ease",
      boxShadow: isHovered ? `0 0 6px ${color}80` : "none",
      transform: isHovered ? "scale(1.1)" : "scale(1)",
    };
  };

  return (
    <div
      style={getStatusStyles(status)}
      title={status || "Sem status"}
      onMouseEnter={(e) => {
        e.target.style.transform = "scale(1.1)";
        e.target.style.boxShadow = `0 0 6px ${getStatusColor(status)}80`;
      }}
      onMouseLeave={(e) => {
        e.target.style.transform = "scale(1)";
        e.target.style.boxShadow = "none";
      }}
    />
  );
}

// Componente para renderizar números grandes
function BigNumber({ number, color = "#333", size = "medium" }) {
  const isSmall = size === "small";
  
  const cardStyles = {
    background: `linear-gradient(135deg, ${color}08 0%, ${color}15 100%)`,
    borderRadius: "6px",
    padding: isSmall ? "4px 3px" : "6px 4px",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
    minWidth: isSmall ? "40px" : "55px",
    maxWidth: isSmall ? "50px" : "65px",
    border: `1px solid ${color}20`,
  };

  const numberStyles = {
    fontSize: isSmall ? "0.9rem" : "1.1rem",
    fontWeight: "700",
    color: color,
    lineHeight: "1.0",
    margin: 0,
  };

  const accentBarStyles = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: `linear-gradient(90deg, ${color} 0%, ${color}80 100%)`,
    borderRadius: "6px 6px 0 0",
  };

  return (
    <div style={cardStyles}>
      <div style={accentBarStyles} />
      <div style={numberStyles}>{number}</div>
    </div>
  );
}

// Função para construir hierarquia de entregáveis
function buildDeliverablesHierarchy(deliverablesData) {
  if (!deliverablesData || deliverablesData.length === 0) {
    return [];
  }
  
  // Obter configuração de hierarquias do primeiro item (todos devem ter a mesma)
  const hierarchyConfig = deliverablesData[0]?.hierarchy_config || ["branch", "workfront", "initiative"];
  
  const branchMap = {};

  deliverablesData.forEach((item) => {
    const {
      branch = "Sem Branch",
      workfront = "Sem Workfront",
      initiative_name = "Sem Iniciativa",
      initiative_id,
      status = "gray",
      completed_deliverables = 0,
      expected_today_deliverables = 0,
      total_deliverables = 0,
    } = item;

    // Construir hierarquia baseada na configuração
    let currentLevel = branchMap;
    let parentKey = null;
    
    // Nível 1: Branch (se configurado)
    if (hierarchyConfig.includes("branch")) {
      if (!currentLevel[branch]) {
        currentLevel[branch] = {
          name: branch,
          status: "gray",
          completed_deliverables: 0,
          expected_today_deliverables: 0,
          total_deliverables: 0,
          children: {},
          level: 0,
          hierarchy_type: "branch"
        };
      }
      currentLevel = currentLevel[branch].children;
      parentKey = branch;
    }
    
    // Nível 2: Workfront (se configurado)
    if (hierarchyConfig.includes("workfront")) {
      if (!currentLevel[workfront]) {
        currentLevel[workfront] = {
          name: workfront,
          status: "gray",
          completed_deliverables: 0,
          expected_today_deliverables: 0,
          total_deliverables: 0,
          children: {},
          level: hierarchyConfig.includes("branch") ? 1 : 0,
          hierarchy_type: "workfront"
        };
      }
      currentLevel = currentLevel[workfront].children;
      parentKey = workfront;
    }
    
    // Nível 3: Initiative (sempre presente)
    if (hierarchyConfig.includes("initiative")) {
      currentLevel[initiative_name] = {
        id: initiative_id,
        name: initiative_name,
        status: status.toLowerCase(),
        completed_deliverables,
        expected_today_deliverables,
        total_deliverables,
        children: [],
        level: hierarchyConfig.length - 1,
        hierarchy_type: "initiative"
      };
    }
  });

  // Função recursiva para calcular agregações
  const calculateAggregations = (levelData, level = 0) => {
    const items = Object.values(levelData);
    
    items.forEach((item) => {
      if (item.children && Object.keys(item.children).length > 0) {
        // Calcular agregações dos filhos
        calculateAggregations(item.children, level + 1);
        
        // Somar valores dos filhos
        const childItems = Object.values(item.children);
        item.completed_deliverables = childItems.reduce(
          (sum, child) => sum + (child.completed_deliverables || 0),
          0
        );
        item.expected_today_deliverables = childItems.reduce(
          (sum, child) => sum + (child.expected_today_deliverables || 0),
          0
        );
        item.total_deliverables = childItems.reduce(
          (sum, child) => sum + (child.total_deliverables || 0),
          0
        );
        item.status = aggregateStatus(childItems.map((child) => child.status));
        
        // Converter children de objeto para array para renderização
        item.children = childItems;
      }
    });
  };

  // Calcular agregações
  calculateAggregations(branchMap);

  // Converter para array
  return Object.values(branchMap);
}

// Função para agregar status (prioridade: red > yellow > green > gray)
function aggregateStatus(statusArray) {
  if (statusArray.includes("red")) return "red";
  if (statusArray.includes("yellow")) return "yellow";
  if (statusArray.includes("green")) return "green";
  return "gray";
}

// Componente de linha da tabela
function DeliverablesRow({
  row,
  level = 0,
  clientColor,
  classes,
  hoveredInitId,
  setHoveredInitId,
  loadingInitId,
  handleInitClickWrapper,
  t,
  hierarchyConfig = ["branch", "workfront", "initiative"]
}) {
  const [open, setOpen] = useState(false);
  const isInitiative = !row.children || row.children.length === 0;
  const isHovered = isInitiative && hoveredInitId === row.id;
  const isLoading = isInitiative && loadingInitId === row.id;

  const nameCellStyles = {
    paddingLeft: level * 16,
    position: "relative",
  };

  const nameTextStyles = {
    fontWeight: level === 0 ? "600" : level === 1 ? "500" : "400",
    fontSize: level === 0 ? "0.8rem" : level === 1 ? "0.75rem" : "0.7rem",
    color: level === 0 ? "#1e293b" : level === 1 ? "#334155" : "#475569",
    transition: "all 0.15s ease",
  };

  const loadingContainerStyles = {
    display: "inline-flex",
    alignItems: "center",
    marginLeft: "6px",
    padding: "2px 4px",
    backgroundColor: "rgba(0, 0, 0, 0.03)",
    borderRadius: "4px",
  };

  // Determinar se deve mostrar o ícone de expansão
  const shouldShowExpandIcon = row.children && row.children.length > 0 && level < hierarchyConfig.length - 1;

  return (
    <>
      <TableRow
        className={level === 0 ? classes.tableRow : classes.childRow}
        style={{
          backgroundColor: getAlphaColor(clientColor, level),
          cursor: isInitiative ? "pointer" : "default",
        }}
        onClick={() => isInitiative && handleInitClickWrapper(row.id)}
        onMouseEnter={() => isInitiative && setHoveredInitId(row.id)}
        onMouseLeave={() => isInitiative && setHoveredInitId(null)}
      >
        {/* Expand Icon */}
        <TableCell className={classes.tableCell} style={{ minWidth: "40px", maxWidth: "40px" }}>
          {shouldShowExpandIcon && (
            <IconButton 
              size="small" 
              onClick={() => setOpen(!open)}
              style={{
                padding: "2px",
                borderRadius: "4px",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "rgba(0, 0, 0, 0.03)";
                e.target.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
                e.target.style.transform = "scale(1)";
              }}
            >
              {open ? <KeyboardArrowUpIcon style={{ fontSize: "16px" }} /> : <KeyboardArrowDownIcon style={{ fontSize: "16px" }} />}
            </IconButton>
          )}
        </TableCell>

        {/* Status */}
        <TableCell className={classes.tableCell} align="center">
          <StatusIndicator status={row.status} size={level === 0 ? "medium" : "small"} />
        </TableCell>

        {/* Name Cell With Indentation */}
        <TableCell className={classes.tableCell}>
          <div style={nameCellStyles}>
            <span style={nameTextStyles}>
              {row.name}
            </span>
            {isLoading && (
              <div style={loadingContainerStyles}>
                <ThreeDots
                  color={clientColor}
                  height={12}
                  width={12}
                  ariaLabel="loading"
                />
                <span style={{ 
                  marginLeft: "4px", 
                  fontSize: "0.65rem", 
                  color: "#64748b",
                  fontWeight: "500"
                }}>
                  Carregando...
                </span>
              </div>
            )}
          </div>
        </TableCell>

        {/* Completed Deliverables */}
        <TableCell className={classes.tableCell} align="center" style={{ textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <BigNumber
              number={row.completed_deliverables || 0}
              color="#10b981"
              size={level === 0 ? "medium" : "small"}
            />
          </div>
        </TableCell>

        {/* Expected Today Deliverables */}
        <TableCell className={classes.tableCell} align="center" style={{ textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <BigNumber
              number={row.expected_today_deliverables || 0}
              color="#f59e0b"
              size={level === 0 ? "medium" : "small"}
            />
          </div>
        </TableCell>

        {/* Total Deliverables */}
        <TableCell className={classes.tableCell} align="center" style={{ textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <BigNumber
              number={row.total_deliverables || 0}
              color="#3b82f6"
              size={level === 0 ? "medium" : "small"}
            />
          </div>
        </TableCell>
      </TableRow>

      {/* Collapsible Children */}
      {row.children && row.children.length > 0 && (
        <TableRow>
          <TableCell
            colSpan={6}
            style={{ paddingBottom: 0, paddingTop: 0 }}
            className={classes.tableCell}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box margin={0.5}>
                <Table size="small" aria-label="child-rows">
                  <TableBody>
                    {row.children.map((child) => (
                      <DeliverablesRow
                        key={child.name}
                        row={child}
                        level={level + 1}
                        clientColor={clientColor}
                        classes={classes}
                        hoveredInitId={hoveredInitId}
                        setHoveredInitId={setHoveredInitId}
                        loadingInitId={loadingInitId}
                        handleInitClickWrapper={handleInitClickWrapper}
                        t={t}
                        hierarchyConfig={hierarchyConfig}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// Função para obter cor com transparência baseada no nível
function getAlphaColor(baseColor, level) {
  // Esquema de cores moderno e executivo
  const modernColors = [
    'rgba(255, 255, 255, 1)',           // branco puro para nível 0
    'rgba(248, 250, 252, 0.95)',        // slate-50 com transparência
    'rgba(241, 245, 249, 0.9)',         // slate-100 com transparência
    'rgba(226, 232, 240, 0.85)',        // slate-200 com transparência
  ];
  
  // Usar cores modernas baseadas no nível
  if (level < modernColors.length) {
    return modernColors[level];
  }
  
  return modernColors[modernColors.length - 1];
}

// Componente principal
export default function DeliverablesOverviewTable({
  selectedSection,
  onInitiativeClick,
  initiativeDetailsOpen,
  clientColor,
  showOnlyCritical: externalShowOnlyCritical,
  isLoading: externalIsLoading, // Nova prop para controlar loading externamente
}) {
  const { t } = useTranslation();
  const { userDetails } = useContext(AuthContext);
  const [deliverablesData, setDeliverablesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showOnlyCritical, setShowOnlyCritical] = useState(externalShowOnlyCritical || false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "total_deliverables",
    direction: "descending",
  });
  const [hoveredInitId, setHoveredInitId] = useState(null);
  const [loadingInitId, setLoadingInitId] = useState(null);
  const [hierarchyConfig, setHierarchyConfig] = useState(["branch", "workfront", "initiative"]);
  // Remover estado de scroll horizontal que não é mais necessário
  // const [showHorizontalScroll, setShowHorizontalScroll] = useState(false);

  // Sincronizar o estado interno com a prop externa
  useEffect(() => {
    // console.log("[DeliverablesOverviewTable] Prop externa alterada:", externalShowOnlyCritical);
    if (externalShowOnlyCritical !== undefined) {
      setShowOnlyCritical(externalShowOnlyCritical);
    }
  }, [externalShowOnlyCritical]);

  // Buscar configuração de hierarquias do cliente
  useEffect(() => {
    let isMounted = true;
    
    async function fetchHierarchyConfig() {
      try {
        const response = await client.get("/api/hierarchy-config/");
        
        if (isMounted && response.data.success) {
          // console.log("[DeliverablesOverviewTable] Configuração de hierarquias:", response.data.data);
          setHierarchyConfig(response.data.data.hierarchy_levels);
        }
      } catch (err) {
        // console.error("Erro ao buscar configuração de hierarquias:", err);
        // Manter configuração padrão em caso de erro
      }
    }

    fetchHierarchyConfig();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Usar loading externo se fornecido, caso contrário usar interno
  const shouldShowLoading = externalIsLoading !== undefined ? externalIsLoading : isLoading;

  // Estilos
  const useStyles = makeStyles((theme) => ({
    table: {
      minWidth: 600,
      maxWidth: "100%",
      borderRadius: "8px",
      overflow: "hidden",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
      border: "1px solid rgba(0, 0, 0, 0.04)",
      // Garantir que a tabela tenha altura mínima para funcionar com scroll
      minHeight: "300px",
      // Garantir que a tabela ocupe todo o espaço disponível
      height: "100%",
    },
    tableHead: {
      background: `linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)`,
      "& th": {
        fontWeight: "600",
        fontSize: "0.75rem",
        padding: "1px 6px",
        borderBottom: `2px solid ${clientColor}`,
        color: "#1e293b",
        textTransform: "uppercase",
        letterSpacing: "0.03em",
        position: "relative",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        minWidth: "80px",
        maxWidth: "120px",
        minHeight: "36px",
        verticalAlign: "middle",
        "&:first-child": {
          minWidth: "40px",
          maxWidth: "40px",
          padding: "1px 4px",
        },
        "&:nth-child(2)": {
          minWidth: "50px",
          maxWidth: "60px",
          padding: "1px 6px",
        },
        "&:nth-child(3)": {
          minWidth: "120px",
          maxWidth: "200px",
        },
        "&:nth-child(4), &:nth-child(5), &:nth-child(6)": {
          minWidth: "90px",
          maxWidth: "110px",
          padding: "1px 4px",
        },
        "&:not(:last-child)::after": {
          content: '""',
          position: "absolute",
          right: 0,
          top: "20%",
          height: "60%",
          width: "1px",
          backgroundColor: "rgba(0, 0, 0, 0.06)",
        },
      },
    },
    tableRow: {
      backgroundColor: "#ffffff",
      transition: "all 0.15s ease",
      "&:nth-of-type(odd)": {
        backgroundColor: "#fafbfc",
      },
      "&:hover": {
        backgroundColor: "#f8fafc",
        transform: "translateY(-1px)",
        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.04)",
      },
    },
    childRow: {
      backgroundColor: "#ffffff",
      transition: "all 0.15s ease",
      "&:nth-of-type(odd)": {
        backgroundColor: "#fafbfc",
      },
      "&:hover": {
        backgroundColor: "#f8fafc",
        transform: "translateY(-1px)",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.03)",
      },
    },
    tableCell: {
      padding: "8px 6px",
      fontSize: "0.75rem",
      borderBottom: "1px solid rgba(0, 0, 0, 0.04)",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      verticalAlign: "middle",
      lineHeight: 1.2,
      color: "#334155",
      transition: "all 0.15s ease",
      // Garantir que as células tenham altura adequada
      minHeight: "40px",
      "&:first-child": {
        padding: "8px 4px",
        minWidth: "40px",
        maxWidth: "40px",
      },
      "&:nth-child(2)": {
        padding: "8px 6px",
        minWidth: "50px",
        maxWidth: "60px",
      },
      "&:nth-child(3)": {
        whiteSpace: "normal",
        wordBreak: "break-word",
        minWidth: "120px",
        maxWidth: "200px",
      },
      "&:nth-child(4), &:nth-child(5), &:nth-child(6)": {
        padding: "8px 4px",
        minWidth: "90px",
        maxWidth: "110px",
      },
    },
    sortIcon: {
      fontSize: "10px",
      marginLeft: "0px",
      color: "#94a3b8",
      transition: "all 0.15s ease",
      flexShrink: 0,
    },
    activeSortIcon: {
      color: clientColor,
      transform: "scale(1.05)",
    },
  }));

  const classes = useStyles();

  // Buscar dados de entregáveis
  useEffect(() => {
    let isMounted = true;
    
    async function fetchDeliverablesData() {
      try {
        setIsLoading(true);
        setError(null);

        // console.log("[DeliverablesOverviewTable] Buscando dados com filtro crítico:", showOnlyCritical);

        const response = await client.post("/api/deliverables-overview-data/", {
          show_only_critical: showOnlyCritical,
        });

        if (isMounted) {
          if (response.data.success) {
            // console.log("[DeliverablesOverviewTable] Dados recebidos:", response.data.data?.length || 0, "itens");
            setDeliverablesData(response.data.data || []);
          } else {
            setError(response.data.error || "Erro ao buscar dados de entregáveis");
          }
        }
      } catch (err) {
        // console.error("Erro ao buscar dados de entregáveis:", err);
        if (isMounted) {
          setError("Erro de conexão com o servidor");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    fetchDeliverablesData();
    
    return () => {
      isMounted = false;
    };
  }, [showOnlyCritical]);

  // Construir hierarquia
  const hierarchyData = useMemo(
    () => buildDeliverablesHierarchy(deliverablesData),
    [deliverablesData]
  );

  // Função de ordenação
  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "ascending"
          ? "descending"
          : "ascending",
    }));
  };

  // Ordenar dados
  const sortedData = useMemo(() => {
    const sortData = (data) => {
      return data.sort((a, b) => {
        const aValue = a[sortConfig.key] || 0;
        const bValue = b[sortConfig.key] || 0;
        
        if (sortConfig.direction === "ascending") {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      }).map(item => ({
        ...item,
        children: item.children ? sortData([...item.children]) : item.children,
      }));
    };

    return sortData([...hierarchyData]);
  }, [hierarchyData, sortConfig]);

  // Handler para clique em iniciativa
  const handleInitClickWrapper = async (initiativeId) => {
    if (!initiativeId) return;
    
    setLoadingInitId(initiativeId);
    try {
      await onInitiativeClick(initiativeId);
    } finally {
      setLoadingInitId(null);
    }
  };

  // Renderizar loading
  if (shouldShowLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
          padding: "20px",
        }}
      >
        <ThreeDots
          color={clientColor}
          height={80}
          width={80}
          ariaLabel="loading"
        />
      </div>
    );
  }

  // Renderizar erro
  if (error) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "30px 16px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            backgroundColor: "#fef2f2",
            border: "2px solid #fecaca",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "12px",
          }}
        >
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            style={{ fontSize: "16px", color: "#ef4444" }}
          />
        </div>
        <div
          style={{
            fontSize: "0.9rem",
            color: "#ef4444",
            fontWeight: "600",
            marginBottom: "8px",
          }}
        >
          Erro ao carregar dados
        </div>
        <div
          style={{
            fontSize: "0.75rem",
            color: "#64748b",
            maxWidth: "300px",
          }}
        >
          {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "6px", height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Tabela - Remover scrollbar interno e ajustar altura */}
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          // Remover overflow e scrollbarWidth que criavam o scrollbar interno
        }}
      >
        <Table 
          className={classes.table} 
          aria-label="deliverables table" 
          size="small" 
          style={{ 
            flex: 1,
            // Garantir que a tabela não tenha scroll interno
            overflow: "visible"
          }}
        >
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.tableCell}></TableCell>
              <TableCell className={classes.tableCell} align="center">
                {t("status")}
              </TableCell>
              <TableCell className={classes.tableCell}>
                {hierarchyConfig.length === 1 ? t("deliverables.initiative") : t("deliverables.name")}
              </TableCell>
              <TableCell
                className={classes.tableCell}
                align="center"
                onClick={() => handleSort("completed_deliverables")}
                style={{ cursor: "pointer" }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "row",
                    gap: "4px",
                    minHeight: "32px",
                  }}
                >
                  <span style={{ fontSize: "0.6rem", fontWeight: "600", lineHeight: "1.1" }}>
                    {t("deliverables.completed")}
                  </span>
                  <FontAwesomeIcon
                    icon={
                      sortConfig.key === "completed_deliverables"
                        ? sortConfig.direction === "ascending"
                          ? faSortUp
                          : faSortDown
                        : faSort
                    }
                    className={
                      sortConfig.key === "completed_deliverables"
                        ? `${classes.sortIcon} ${classes.activeSortIcon}`
                        : classes.sortIcon
                    }
                  />
                </div>
              </TableCell>
              <TableCell
                className={classes.tableCell}
                align="center"
                onClick={() => handleSort("expected_today_deliverables")}
                style={{ cursor: "pointer" }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "row",
                    gap: "4px",
                    minHeight: "32px",
                  }}
                >
                  <span style={{ fontSize: "0.6rem", fontWeight: "600", lineHeight: "1.1" }}>
                    {t("deliverables.expected_today")}
                  </span>
                  <FontAwesomeIcon
                    icon={
                      sortConfig.key === "expected_today_deliverables"
                        ? sortConfig.direction === "ascending"
                          ? faSortUp
                          : faSortDown
                        : faSort
                    }
                    className={
                      sortConfig.key === "expected_today_deliverables"
                        ? `${classes.sortIcon} ${classes.activeSortIcon}`
                        : classes.sortIcon
                    }
                  />
                </div>
              </TableCell>
              <TableCell
                className={classes.tableCell}
                align="center"
                onClick={() => handleSort("total_deliverables")}
                style={{ cursor: "pointer" }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "row",
                    gap: "4px",
                    minHeight: "32px",
                  }}
                >
                  <span style={{ fontSize: "0.6rem", fontWeight: "600", lineHeight: "1.1" }}>
                    {t("deliverables.total")}
                  </span>
                  <FontAwesomeIcon
                    icon={
                      sortConfig.key === "total_deliverables"
                        ? sortConfig.direction === "ascending"
                          ? faSortUp
                          : faSortDown
                        : faSort
                    }
                    className={
                      sortConfig.key === "total_deliverables"
                        ? `${classes.sortIcon} ${classes.activeSortIcon}`
                        : classes.sortIcon
                    }
                  />
                </div>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.length > 0 ? (
              sortedData.map((branch) => (
                <DeliverablesRow
                  key={branch.name}
                  row={branch}
                  level={0}
                  clientColor={clientColor}
                  classes={classes}
                  hoveredInitId={hoveredInitId}
                  setHoveredInitId={setHoveredInitId}
                  loadingInitId={loadingInitId}
                  handleInitClickWrapper={handleInitClickWrapper}
                  t={t}
                  hierarchyConfig={hierarchyConfig}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" className={classes.tableCell}>
                  <div
                    style={{
                      padding: "20px 16px",
                      textAlign: "center",
                      color: "#64748b",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "500",
                        marginBottom: "4px",
                      }}
                    >
                      Nenhum dado disponível
                    </div>
                    <div
                      style={{
                        fontSize: "0.7rem",
                        color: "#94a3b8",
                      }}
                    >
                      {t("deliverables.no_data_available")}
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
